import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  Bell, 
  Radio, 
  UserPlus, 
  AlertTriangle,
  Calendar,
  Check,
  X,
  Share2,
  CheckCircle2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const NotificationBell = () => {
  const { user, getAuthHeader } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);


  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/notifications`,
        { headers: getAuthHeader() }
      );
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const markAsRead = async (notificationId) => {
    try {
      await axios.put(
        `${API_URL}/api/notifications/${notificationId}/read`,
        {},
        { headers: getAuthHeader() }
      );
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(
        `${API_URL}/api/notifications/read-all`,
        {},
        { headers: getAuthHeader() }
      );
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'stream_live':
        return <Radio className="w-5 h-5 text-red-500" />;
      case 'new_follower':
        return <UserPlus className="w-5 h-5 text-[#7C3AED]" />;
      case 'stream_auto_ended':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'match_scheduled':
        return <Calendar className="w-5 h-5 text-green-500" />;
      case 'restream_started':
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case 'restream_stopped':
        return <Share2 className="w-5 h-5 text-gray-400" />;
      case 'restream_failed':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      default:
        return <Bell className="w-5 h-5 text-gray-400" />;
    }
  };

  const getNotificationLink = (notification) => {
    if (notification.type === 'stream_live' && notification.data?.stream_id) {
      return `/stream/${notification.data.stream_id}`;
    }
    if (notification.type === 'new_follower' && notification.data?.follower_username) {
      return `/profile/${notification.data.follower_username}`;
    }
    if (notification.type === 'match_scheduled') {
      return `/calendario`;
    }
    if (notification.type?.startsWith('restream_')) {
      return `/go-live`;
    }
    return null;
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = (now - date) / 1000;

    if (diff < 60) return 'Ahora';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full hover:bg-white/5 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-gray-400" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-[#0A0A0A] border border-white/10 rounded-2xl shadow-xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <h3 className="font-semibold text-white">Notificaciones</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-[#7C3AED] hover:text-[#7C3AED]/80"
              >
                Marcar todas como leídas
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No tienes notificaciones</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const link = getNotificationLink(notification);
                const NotificationContent = (
                  <div
                    className={`flex items-start gap-3 p-4 hover:bg-white/5 transition-colors border-b border-white/5 ${
                      !notification.read ? 'bg-[#7C3AED]/5' : ''
                    }`}
                    onClick={() => !notification.read && markAsRead(notification.id)}
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">
                        {notification.title}
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatTime(notification.created_at)}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className="w-2 h-2 rounded-full bg-[#7C3AED] flex-shrink-0 mt-2" />
                    )}
                  </div>
                );

                return link ? (
                  <Link key={notification.id} to={link} onClick={() => setIsOpen(false)}>
                    {NotificationContent}
                  </Link>
                ) : (
                  <div key={notification.id}>{NotificationContent}</div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
